import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import useGenerateExternalUrl from 'hooks/useGenerateExternalUrl';

/**
 * Append `embed=1` to the Folder View URL. `embed=1` tells the frontend to
 * render the page chrome-less (no left nav) — see apps/firm AppLayout. Built via
 * the URL API so an existing query/hash on the generated URL is preserved
 * correctly. Falls back to the raw URL if it can't be parsed (the hook always
 * returns a valid absolute URL, so this is purely defensive).
 */
export function buildEmbedUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('embed', '1');
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * "Folder View" affordance for the account Files tab
 * (/accountdetails/:id/files): opens the derived, read-only folder view for the
 * account — its files organized into Tax, Bookkeeping, Payroll, PERM, and Unresolved
 * tree — in a modal iframe.
 *
 * Account-scoped ONLY: Folder View is derived per account, so there is no
 * firm-wide view in v1; without an `accountId` (the firm-wide /files page) the
 * button is not shown. Dark-shipped behind the `enableFolderView` LaunchDarkly
 * flag — the SAME flag the backend read endpoints enforce — so the button and
 * the data it opens are gated together. Unlike SmartVault there is no
 * per-account precondition (any account's files can be organized), so visibility
 * is the flag alone, with no backend availability probe.
 *
 * The view itself is liscio-frontend's, embedded via `?embed=1`. The iframe is
 * kept mounted (keepMounted) but is given a `src` only once the user shows intent
 * — hovering/focusing the button (a debounced prefetch) or opening the modal — so
 * the embedded app boots (and runs its now-cheap tree query) on intent, NOT on
 * every Files-page visit, which would boot a second app + call the folder-view
 * endpoints for the whole flagged firm before anyone reaches for the button.
 * Warming on hover means the click usually reveals an already-loaded view;
 * keepMounted then keeps it warm for instant reopen.
 *
 * The embedded view authenticates via the shared `*.liscio.me` cookie, which can
 * fail in a third-party iframe (Safari ITP, a CSP frame block, a network error),
 * leaving the modal blank — so we always offer an "Open in new tab" escape hatch
 * (a first-party load, which authenticates reliably) and surface a message if
 * the frame errors outright.
 */
function FolderViewButton({ accountId, accountName }) {
  const { enableFolderView } = useFlags();
  const [open, setOpen] = useState(false);
  // Gate the iframe `src` on first intent (hover/focus warm — see `warm` — or
  // click): until then we render no src, so the embedded app never boots (never
  // hits the folder-view endpoints) just from the button being on the page. Stays
  // true once warmed so keepMounted can make open/reopen instant.
  const [hasOpened, setHasOpened] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Prefetch-on-intent: hovering/focusing the button boots the embedded app (and
  // runs its now-cheap tree query) ahead of the click, debounced so a pointer
  // merely passing over the button doesn't boot a second app. Once warmed it stays
  // mounted, so the click just reveals an already-loaded view.
  const warmTimer = useRef(null);

  const warm = useCallback(() => {
    if (warmTimer.current) return;
    warmTimer.current = setTimeout(() => {
      warmTimer.current = null;
      setHasOpened(true);
    }, 120);
  }, []);

  const cancelWarm = useCallback(() => {
    if (!warmTimer.current) return;
    clearTimeout(warmTimer.current);
    warmTimer.current = null;
  }, []);

  // Don't leave a pending warm timer firing setState after unmount.
  useEffect(() => () => clearTimeout(warmTimer.current), []);

  const handleOpen = () => {
    cancelWarm();
    setHasOpened(true);
    setOpen(true);
  };

  // A clean close keeps the frame warm (hasOpened stays true) for instant reopen.
  // But after a load error the keepMounted frame is dead, so drop hasOpened +
  // loadError on close — otherwise reopening never re-navigates and the user is
  // stuck on the fallback overlay until a full page refresh.
  const handleClose = () => {
    setOpen(false);
    if (loadError) {
      setLoadError(false);
      setHasOpened(false);
    }
  };

  // React's synthetic event system doesn't wire onLoad/onError for <iframe> the
  // way it does for media/img (onError on an iframe never fires), so bind the
  // load/error listeners natively via the ref. Best-effort: a cross-origin frame
  // blocked by Safari ITP / a CSP frame-block usually "loads" blank WITHOUT
  // emitting `error`, so the always-visible "Open in new tab" escape hatch stays
  // the primary fallback — this just surfaces the overlay for frames that do
  // raise an error (e.g. a network failure).
  const bindIframe = useCallback((node) => {
    if (!node) return;
    node.addEventListener('load', () => setLoadError(false));
    node.addEventListener('error', () => setLoadError(true));
  }, []);

  const folderViewUrl = useGenerateExternalUrl(
    accountId
      ? `/external/folder-view/account/${accountId}`
      : '/external/folder-view',
  );

  if (!accountId || enableFolderView !== true) return null;

  const embedUrl = buildEmbedUrl(folderViewUrl);

  return (
    <Stack direction="row" alignItems="center">
      <Divider
        orientation="vertical"
        sx={{ height: 28, mx: 3.75, borderRightWidth: 2 }}
      />
      <Button
        type="button"
        variant="outlined"
        color="primary"
        startIcon={<FolderOpenOutlinedIcon />}
        onClick={handleOpen}
        onMouseEnter={warm}
        onMouseLeave={cancelWarm}
        onFocus={warm}
        onBlur={cancelWarm}
        data-testid="files__folder_view_btn"
        sx={{
          // Sized to sit just shy of the neighboring filter chips' height.
          height: 40,
          px: 1.25,
          borderWidth: 2,
          fontWeight: 600,
          '&:hover': { borderWidth: 2 },
        }}
      >
        Folder View
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        // keepMounted so reopen is instant after the first open; the iframe gets
        // a `src` only once opened (see hasOpened) so it never loads pre-click.
        keepMounted
        maxWidth={false}
        // Right-align over the content area so the modal clears the left main
        // nav rather than overlapping it.
        sx={{
          '& .MuiDialog-container': { justifyContent: 'flex-end', pr: 5 },
        }}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 300px)',
            maxWidth: 1500,
            height: '90vh',
            m: 0,
          },
        }}
        aria-labelledby="folder-view-title"
      >
        <DialogTitle
          id="folder-view-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.625}>
            <FolderOpenOutlinedIcon color="primary" />
            <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
              Folder View
              {accountName && (
                <Typography
                  component="span"
                  variant="h6"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}
                >
                  {' '}
                  — {accountName}
                </Typography>
              )}
            </Typography>
            {/* First-party escape hatch: opens the folder view in a new tab.
                Always available, so a blocked/blank iframe is never a dead end. */}
            <Tooltip title="Open in new tab">
              <IconButton
                component="a"
                href={folderViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                size="small"
                color="primary"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, flex: 1, position: 'relative' }}>
          <iframe
            ref={bindIframe}
            src={hasOpened ? embedUrl : undefined}
            title="Folder View"
            style={{ width: '100%', height: '100%', border: 0 }}
          />
          {loadError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.625,
                bgcolor: 'background.paper',
                p: 1.875,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1">
                The folder view couldn’t be loaded here.
              </Typography>
              <Link
                href={folderViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3125,
                  fontWeight: 600,
                }}
              >
                Open Folder View in a new tab
                <OpenInNewIcon fontSize="small" />
              </Link>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

export default FolderViewButton;
